import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@org/ui';
import { Camera, RefreshCw, SwitchCamera, Copy, Check } from 'lucide-react';
import {
  captureFrame,
  constraintsFor,
  defaultFacingFor,
  flipFacing,
  isCameraSupported,
  mapCameraError,
  overlayFor,
  shouldMirrorPreview,
  stopStream,
  type CameraErrorKind,
  type Facing,
  type PhotoKind,
} from '../../lib/camera';
import { P, btnGhost, btnRed } from '../products/theme';

export type CapturedPhoto = { imageBase64: string; mimeType: 'image/jpeg'; previewUrl: string };

type Props = {
  kind: PhotoKind;
  /** Called with the JPEG the moment the shutter is pressed. */
  onCapture: (photo: CapturedPhoto) => void;
  disabled?: boolean;
};

type Phase = 'starting' | 'live' | 'error';

/**
 * Live camera: opens the device camera (front for selfies, back for
 * documents, with a flip button), draws a dashed guide over the preview and
 * snapshots the frame on "Tirar foto". There is deliberately no file picker:
 * the photo must be taken here and now.
 */
export function CameraCapture({ kind, onCapture, disabled }: Props) {
  const { t } = useTranslation('p2e');
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facing, setFacing] = useState<Facing>(() => defaultFacingFor(kind));
  const [phase, setPhase] = useState<Phase>('starting');
  const [error, setError] = useState<CameraErrorKind | null>(null);
  const [canFlip, setCanFlip] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Each photo kind starts on its natural camera.
  useEffect(() => {
    setFacing(defaultFacingFor(kind));
  }, [kind]);

  const stop = useCallback(() => {
    stopStream(streamRef.current);
    streamRef.current = null;
    const video = videoRef.current;
    if (video) video.srcObject = null;
  }, []);

  // Open the camera whenever the facing changes or the student retries.
  useEffect(() => {
    let cancelled = false;
    if (!isCameraSupported()) {
      setError('unsupported');
      setPhase('error');
      return;
    }
    setPhase('starting');
    setError(null);
    stop();

    navigator.mediaDevices
      .getUserMedia(constraintsFor(facing))
      .then(async (stream) => {
        if (cancelled) {
          stopStream(stream);
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          // iOS Safari ignores the autoplay attribute for camera streams: an
          // explicit play() after srcObject is what actually starts it.
          try {
            await video.play();
          } catch {
            // AbortError when the element re-renders mid-play; the stream is
            // still attached and the next frame plays.
          }
        }
        setPhase('live');
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          if (!cancelled) setCanFlip(devices.filter((d) => d.kind === 'videoinput').length > 1);
        } catch {
          // enumerateDevices is optional sugar for the flip button.
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(mapCameraError(err));
        setPhase('error');
      });

    return () => {
      cancelled = true;
      stop();
    };
  }, [facing, attempt, stop]);

  // Belt and braces: leaving the page (tab close, navigation) releases the camera.
  useEffect(() => {
    window.addEventListener('pagehide', stop);
    return () => window.removeEventListener('pagehide', stop);
  }, [stop]);

  const mirror = shouldMirrorPreview(facing);

  const shoot = () => {
    const video = videoRef.current;
    if (!video || phase !== 'live') return;
    try {
      // Selfie: save what she saw (mirrored). Document: as-is, text readable.
      const photo = captureFrame(video, { mirror: mirror && kind === 'selfie' });
      stop();
      onCapture(photo);
    } catch {
      setError('unknown');
      setPhase('error');
    }
  };

  if (phase === 'error' && error) {
    return <CameraError kind={error} onRetry={() => setAttempt((n) => n + 1)} />;
  }

  const overlay = overlayFor(kind);

  return (
    <div>
      <div style={frameStyle} data-testid="camera-frame">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          aria-label={t('access.camera.previewAlt')}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            transform: mirror ? 'scaleX(-1)' : undefined,
            background: P.black,
          }}
        />
        <div aria-hidden style={overlay === 'oval' ? ovalGuide : cardGuide} />
        {phase === 'starting' && (
          <div role="status" style={startingStyle}>
            {t('access.camera.starting')}
          </div>
        )}
        {canFlip && phase === 'live' && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('access.camera.flip')}
            onClick={() => setFacing((f) => flipFacing(f))}
            className="absolute right-2.5 top-2.5 h-9 w-9 rounded-full border-white/70 bg-white/85 text-[#2D1A1F] hover:bg-white"
          >
            <SwitchCamera size={18} aria-hidden />
          </Button>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
        <Button
          type="button"
          variant="red"
          aria-label={t('access.camera.shutter')}
          onClick={shoot}
          disabled={disabled || phase !== 'live'}
          className="h-[68px] w-[68px] rounded-full border-4 border-[#F8D7DD] p-0 shadow-md"
          style={{ background: P.maxRed }}
        >
          <Camera size={26} aria-hidden />
        </Button>
      </div>
      <p style={{ textAlign: 'center', margin: '8px 0 0', fontSize: 13, color: P.midText }}>{t('access.camera.shutter')}</p>
    </div>
  );
}

function CameraError({ kind, onRetry }: { kind: CameraErrorKind; onRetry: () => void }) {
  const { t } = useTranslation('p2e');
  const [copied, setCopied] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const showLink = kind === 'no-camera' || kind === 'unsupported';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: the URL is still printed on screen to copy by hand.
    }
  };

  return (
    <div role="alert" style={errorBox} data-testid={`camera-error-${kind}`}>
      <p style={{ margin: '0 0 6px', fontWeight: 600, color: P.darkText }}>{t(`access.camera.errors.${kind}.title`)}</p>
      <p style={{ margin: '0 0 12px', fontSize: 14, lineHeight: 1.5, color: P.midText }}>{t(`access.camera.errors.${kind}.body`)}</p>
      {showLink && url && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <code style={urlStyle}>{url}</code>
          <Button type="button" variant="outline" size="sm" onClick={copy} aria-label={t('access.camera.copyLink')}>
            {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
            {copied ? t('access.camera.copied') : t('access.camera.copyLink')}
          </Button>
        </div>
      )}
      <Button type="button" variant={showLink ? 'outline' : 'red'} className="products-btn" style={showLink ? btnGhost : btnRed} onClick={onRetry}>
        <RefreshCw size={16} aria-hidden /> {t('access.camera.retry')}
      </Button>
    </div>
  );
}

const frameStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  aspectRatio: '3 / 4',
  maxHeight: 440,
  overflow: 'hidden',
  borderRadius: 14,
  border: `1px solid ${P.lightBorder}`,
  background: P.black,
  boxShadow: `0 8px 20px ${P.shadow}`,
};

const ovalGuide: CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  width: '62%',
  height: '58%',
  transform: 'translate(-50%, -50%)',
  borderRadius: '50%',
  border: `3px dashed ${P.charm}`,
  boxShadow: '0 0 0 9999px rgba(26, 16, 24, 0.28)',
  pointerEvents: 'none',
};

const cardGuide: CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  width: '86%',
  aspectRatio: '1.586 / 1',
  transform: 'translate(-50%, -50%)',
  borderRadius: 10,
  border: `3px dashed ${P.charm}`,
  boxShadow: '0 0 0 9999px rgba(26, 16, 24, 0.28)',
  pointerEvents: 'none',
};

const startingStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: P.white,
  fontSize: 14,
  background: 'rgba(26, 16, 24, 0.45)',
};

const errorBox: CSSProperties = {
  padding: '14px 14px 16px',
  borderRadius: 12,
  border: `1px solid ${P.lightBorder}`,
  background: P.blush,
};

const urlStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 12,
  padding: '6px 8px',
  borderRadius: 6,
  background: P.white,
  border: `1px solid ${P.lightBorder}`,
  color: P.darkText,
};
